import type { OutgoingStatisticsResponse } from '../../../store/reducers/OutgoingStatisticsSlice';

import type { OutgoingStatisticMetrics } from './outgoingStatisticMetrics';
import type { OutgoingStatisticRow } from './statisticRowTypes';
import {
  createFixedPercentMetricRow,
  createMetricRow,
  createRatioRow,
  createZeroPercentMetricRow,
} from './statisticRowFactory';

const SECTION = 'Эффективность обзвона';

const createResultChildren = ({
  keyPrefix,
  businessValue,
  technicalValue,
  denominator,
}: {
  readonly keyPrefix: string;
  readonly businessValue: number;
  readonly technicalValue: number;
  readonly denominator: number;
}): OutgoingStatisticRow[] => [
  createMetricRow({
    key: `${keyPrefix}-target`,
    section: SECTION,
    parameter: 'целевой результат',
    value: businessValue,
    denominator,
    level: 1,
  }),
  createMetricRow({
    key: `${keyPrefix}-technical`,
    section: SECTION,
    parameter: 'технический результат',
    value: technicalValue,
    denominator,
    level: 1,
  }),
];

const createAttemptResultRow = ({
  key,
  parameter,
  value,
  businessValue,
  technicalValue,
  denominator,
}: {
  readonly key: string;
  readonly parameter: string;
  readonly value: number;
  readonly businessValue: number;
  readonly technicalValue: number;
  readonly denominator: number;
}): OutgoingStatisticRow =>
  createMetricRow({
    key,
    section: SECTION,
    parameter,
    value,
    denominator,
    children: createResultChildren({
      keyPrefix: key,
      businessValue,
      technicalValue,
      denominator,
    }),
  });

export const buildEfficiencyTaskRows = (
  statistic: OutgoingStatisticsResponse,
  metrics: OutgoingStatisticMetrics,
): OutgoingStatisticRow[] => [
  createRatioRow({
    key: 'efficiency-averageAttempts',
    section: SECTION,
    parameter: 'Среднее число попыток до результата',
    numerator: metrics.processedTasksAttemptCount,
    denominator: metrics.processedTasksCount,
  }),
  createAttemptResultRow({
    key: 'efficiency-oneAttemptTasksCount',
    parameter: 'Результат с 1 попытки',
    value: metrics.oneAttemptTasksCount,
    businessValue: statistic.oneAttemptBusinessResultTasksCount,
    technicalValue: statistic.oneAttemptTechnicalResultTasksCount,
    denominator: metrics.processedTasksCount,
  }),
  createAttemptResultRow({
    key: 'efficiency-twoAttemptTasksCount',
    parameter: 'Результат со 2 попытки',
    value: metrics.twoAttemptTasksCount,
    businessValue: statistic.twoAttemptBusinessResultTasksCount,
    technicalValue: statistic.twoAttemptTechnicalResultTasksCount,
    denominator: metrics.processedTasksCount,
  }),
  createAttemptResultRow({
    key: 'efficiency-threeAndMoreAttemptTasksCount',
    parameter: 'Результат с 3+ попыток',
    value: metrics.threeAndMoreAttemptTasksCount,
    businessValue: statistic.threeAndMoreAttemptBusinessResultTasksCount,
    technicalValue: statistic.threeAndMoreAttemptTechnicalResultTasksCount,
    denominator: metrics.processedTasksCount,
  }),
  createMetricRow({
    key: 'efficiency-noAttemptTasksCount',
    section: SECTION,
    parameter: 'Результат получен без попыток звонков (из МИС)',
    value: metrics.noAttemptTasksCount,
    denominator: metrics.processedTasksCount,
  }),
];

export const buildEfficiencyCallRows = (
  statistic: OutgoingStatisticsResponse,
  metrics: OutgoingStatisticMetrics,
): OutgoingStatisticRow[] => [
  createFixedPercentMetricRow({
    key: 'efficiency-processedTasksAttemptCount',
    section: SECTION,
    parameter: 'Всего звонков по обработанным заданиям',
    value: metrics.processedTasksAttemptCount,
  }),
  createAttemptResultRow({
    key: 'efficiency-oneAttemptTasksAttemptCount',
    parameter: 'Звонки в заданиях с результатом за 1 попытку',
    value:
      metrics.oneAttemptBusinessResultTasksAttemptCount +
      metrics.oneAttemptTechnicalResultTasksAttemptCount,
    businessValue: metrics.oneAttemptBusinessResultTasksAttemptCount,
    technicalValue: metrics.oneAttemptTechnicalResultTasksAttemptCount,
    denominator: metrics.processedTasksAttemptCount,
  }),
  createAttemptResultRow({
    key: 'efficiency-twoAttemptTasksAttemptCount',
    parameter: 'Звонки в заданиях с результатом за 2 попытки',
    value:
      metrics.twoAttemptBusinessResultTasksAttemptCount +
      metrics.twoAttemptTechnicalResultTasksAttemptCount,
    businessValue: metrics.twoAttemptBusinessResultTasksAttemptCount,
    technicalValue: metrics.twoAttemptTechnicalResultTasksAttemptCount,
    denominator: metrics.processedTasksAttemptCount,
  }),
  createAttemptResultRow({
    key: 'efficiency-threeAndMoreAttemptTasksAttemptCount',
    parameter: 'Звонки в заданиях с результатом за 3+ попытки',
    value: metrics.threeAndMoreAttemptTasksAttemptCount,
    businessValue: statistic.threeAndMoreAttemptBusinessResultTasksAttemptCount,
    technicalValue:
      statistic.threeAndMoreAttemptTechnicalResultTasksAttemptCount,
    denominator: metrics.processedTasksAttemptCount,
  }),
  createZeroPercentMetricRow({
    key: 'efficiency-noAttemptTasksAttemptCount',
    section: SECTION,
    parameter: 'Результат получен без попыток звонков (из МИС)',
    value: 0,
  }),
];
